import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { MailCheck, KeyRound } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Btn, Field, Input } from "../components/kit";

const Frame = ({ children }) => (
  <div className="min-h-screen bg-[#0A0A0A] flex flex-col justify-center px-6">
    <div className="w-full max-w-sm mx-auto fade-in space-y-5">{children}</div>
  </div>
);

export function ForgotPassword() {
  const [sp] = useSearchParams();
  const back = sp.get("portal") === "admin" ? "/admin/login" : "/login";
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(data.message);
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Frame>
      <h1 className="font-head text-4xl font-bold uppercase">Reset password</h1>
      {sent ? (
        <div className="space-y-4" data-testid="forgot-sent">
          <div className="flex gap-3 text-sm text-neutral-300"><MailCheck className="w-5 h-5 text-[#10B981] shrink-0" />{sent} Check your inbox and spam folder.</div>
          <Link to={back}><Btn variant="outline" className="w-full">Back to sign in</Btn></Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <p className="text-sm text-neutral-400">Enter your account email. We'll send a link to choose a new password.</p>
          <Field label="Email"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email-input" /></Field>
          {err && <div className="text-sm text-red-400" data-testid="forgot-error">{err}</div>}
          <Btn size="lg" className="w-full" loading={busy} data-testid="forgot-submit-btn">Send reset link</Btn>
          <Link to={back} className="block text-center text-sm text-neutral-500">Back to sign in</Link>
        </form>
      )}
    </Frame>
  );
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const token = sp.get("token") || "";
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw !== pw2) return setErr("Passwords don't match");
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/auth/reset-password", { token, password: pw });
      nav(data.role === "admin" ? "/admin/login" : "/login", { replace: true, state: { reset: true } });
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Frame>
      <h1 className="font-head text-4xl font-bold uppercase flex items-center gap-3"><KeyRound className="w-7 h-7 text-[#FF6B00]" />New password</h1>
      {!token ? (
        <p className="text-sm text-red-400" data-testid="reset-missing-token">This link is incomplete. <Link to="/forgot-password" className="text-[#FF6B00]">Request a new one</Link>.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <Field label="New password" hint="At least 8 characters"><Input type="password" autoComplete="new-password" minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="reset-password-input" /></Field>
          <Field label="Repeat password"><Input type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} required data-testid="reset-password-confirm-input" /></Field>
          {err && <div className="text-sm text-red-400" data-testid="reset-error">{err}</div>}
          <Btn size="lg" className="w-full" loading={busy} data-testid="reset-submit-btn">Save new password</Btn>
        </form>
      )}
    </Frame>
  );
}
